import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from "@angular/core"
import { MatTableDataSource } from "@angular/material/table";
import { CURRENT_STATE_PROJECT_KEY, getInsightsWithoutCreate, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE } from "../../common/linqset-model";
import { MatSort } from "@angular/material/sort";
import { USER_ROLE } from "../../common/user";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../../ui-components/dialogs/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { OrganisationService } from "../../organisation/organisation.service";
import { ModelService } from "../../organisation/models/model-service";
import { AppComponent } from "../../app.component";
import { ASSET_CLASSIFICATION_LABELS, InformationAsset, NodeGroupModelInsights } from "../../common/organisation";
import { editInformationAsset } from "../../model-components/information-asset/information-asset-dialog.component";
import { OrganisationModelReader } from "../../model-services/organisation-model-reader.service";
import { FormattingService } from "../../linq-utils/formatting/formatting.service";

// The organisation's information assets with their register (owner, classification, system of record) and their
// figures in what is shown: states, items, derived value, value and cost. Those who can change the organisation can add,
// edit and delete assets from it. Picking an asset shows its states in a model, or the models it is in
@Component({
    selector: 'app-information-asset-table',
    templateUrl: './information-asset-table.html',
    styleUrls: ['./information-asset-table.css'],
    standalone: false
})
export class InformationAssetTableComponent implements OnInit, OnChanges {
    @Input() linqset: Linqset
    @Input() projectKey:string

    @Output() selectNodeEvent = new EventEmitter<string>();

    // few enough to fit the half-width card on the organisation home
    columns = ['name', 'owner', 'classification', 'systemOfRecord', 'numberOfStates', 'derivedValue', 'value', 'cost', 'actions'];
    headings = ['Name', 'Owner', 'Class', 'System of record', 'States', 'Derived', 'Value', 'Cost', ''];
    // the columns an asset's states, or the models it is in, show
    detailColumns = ['icon', 'name', 'numberOfStates', 'numberProcessed', 'value', 'cost'];
    textColumns = new Set(['name', 'owner', 'classification', 'systemOfRecord'])

    assetsource = new MatTableDataSource<any>();
    datasource = new MatTableDataSource<any>();

    @ViewChild(MatSort) sort: MatSort

    expandedElement:any
    private organisationNodes:LinqsetNode[] = []

    constructor(private dialog:MatDialog, private organisationService:OrganisationService, private modelService:ModelService, private organisationModel:OrganisationModelReader, private formatter:FormattingService) {

    }

    ngOnChanges(changes) {
        if (changes["linqset"] || changes["organisation"] || changes["projectKey"])
            this.ngOnInit()
    }

    ngOnInit(): void {
        this.expandedElement = null
        this.build()
        void this.loadOrganisationNodes()
    }

    // the rows: each asset with its register and its figures in what is shown
    private build() {
        const {organisation, insights} = this.linqset || {} as Linqset
        const type = NODE_TYPE.ASSET
        const nameOf = (key?:string|null) => key ? this.organisationNodes.find(n => n._key === key)?.name || '' : ''
        const row = (asset:InformationAsset, iaInsights?:NodeGroupModelInsights) => ({
            ...iaInsights, ...asset, type, asset,
            owner: nameOf(asset.ownerKey),
            classification: asset.classification ? ASSET_CLASSIFICATION_LABELS[asset.classification] : '',
            systemOfRecord: nameOf(asset.systemOfRecordKey),
            derivedValue: iaInsights?.assetValue?.derivedValue,
            cost: iaInsights?.allocatedCostsByType?.total || 0,
        })

        let rows:any[] = []
        if (organisation?.informationAssets) {
            const assetInsights = insights?.informationAssets || []
            if (this.linqset.isProcessModel()) {
                // the assets in the model
                rows = assetInsights.reduce((list, iaInsights) => {
                    const asset = organisation.informationAssets.find(ia => ia._key === iaInsights.groupKey)
                    if (asset) list.push(row(asset, iaInsights))
                    return list
                }, [])
            }else {
                rows = organisation.informationAssets.map(asset => row(asset, assetInsights.find(i => i.groupKey === asset._key)))
            }
        }
        this.datasource.data = rows
        this.datasource.sort = this.sort;
    }

    // the roles and systems an owner and system of record are named from, and picked from when editing: those in the
    // linqset where it has them (the organisation model), else read from the organisation model, sharing the read with
    // the other views loading it together (as the home screen's overview does)
    private async loadOrganisationNodes() {
        const organisation = this.linqset?.organisation
        if (!organisation?.informationAssets?.length) return
        const workspaceKey = this.linqset.workspaceKey || CURRENT_STATE_PROJECT_KEY
        const own = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM)
        if (this.linqset.isOrganisationModel() && own.length) {
            this.organisationNodes = this.linqset.nodes
        }else {
            const graph = await this.organisationModel.read(organisation._key, workspaceKey).catch(() => undefined)
            this.organisationNodes = graph?.nodes || []
        }
        this.build()
    }

    isHelpOn() {
        return AppComponent.help
    }

    // a value or cost, shortened to fit (for example 325K), with the full amount in its tooltip
    amount(column:string, value:number|undefined) {
        const v = Math.round(value || 0)
        return column === 'cost' ? this.formatter.currencySymbol + this.formatter.formatAbbreviatedNumber(v) : this.formatter.formatAbbreviatedNumber(v)
    }

    fullAmount(column:string, value:number|undefined) {
        return column === 'cost' ? this.formatter.formatCost(value || 0) : this.formatter.formatNumber(value || 0)
    }

    isText(column:string) {
        return this.textColumns.has(column)
    }

    selectInfo(info:any) {
        if (this.linqset.isProcessModel()) {
            this.selectNodeEvent.emit(info._key)
        }else {
            const model = this.linqset.organisation.models.find(m => m._key === info.modelKey)
            if (model) {
                this.modelService.editModel(this.linqset.organisation, model, this.projectKey)
            }
        }
    }

    // assets can be changed in current state, by those who can change the organisation
    canEdit() {
        const role = this.linqset?.organisation.role
        return (role === USER_ROLE.WRITE || role === USER_ROLE.ADMIN) && (this.projectKey || CURRENT_STATE_PROJECT_KEY) === CURRENT_STATE_PROJECT_KEY
    }

    canDelete() {
        return this.canEdit()
    }

    // the asset's name, description and register, in the asset dialog; or a new asset
    async editAsset(row?:any) {
        if (!this.canEdit()) return
        const saved = await editInformationAsset(this.dialog, this.organisationService, row?.asset, this.organisationNodes)
        if (saved) this.build()
    }

    deleteAsset(row) {
        const models = this.getModels(row)

        const names = models.map(m => m.name).join(', ')

        let message = models.length>0?`This asset is used in the following models ${names}. Are you sure you want to delete this asset?`:
            `Are you sure you want to delete this asset?`
        const dialogData = new ConfirmDialogModel("Confirm Action", [message], "delete", false, "Yes", "No");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "400px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                if (dialogResult.button === "Yes") {
                    await this.organisationService.deleteInformationAsset(row._key)
                    this.build()
                }
            }
        })

    }

    sortChange(event) {
        if (event.direction!=='') {
            // text in alphabetical order, ignoring case; numbers by size, those with none last
            const value = (row:any) => this.isText(event.active) ? String(row[event.active] || '').toLowerCase() : (row[event.active] ?? -1)
            this.datasource.data = this.datasource.data.sort((a,b) => {
                if (value(a) > value(b))
                    return (event.direction === "asc")?1:-1
                else return (event.direction === "asc")?-1:1
            })
        }
    }

    isExpanded(row:any) {
        //return false
        return this.expandedElement !== null && this.expandedElement._key === row._key
    }

    expandAsset(row:any) {
        this.expandedElement = this.expandedElement === row ? null : row

        if (this.expandedElement) {
            if (this.linqset.isProcessModel()) {
                this.assetsource.data = this.linqset.nodes.filter(n => n.informationAssetKey && n.informationAssetKey === row._key).map(n => {
                    const type = n.type
                    const insights = getInsightsWithoutCreate(n, this.linqset)
                    return {type, name: n.name, ...insights, cost:insights.allocatedCostsByType.total, _key:n._key}
                })
            }else {
                this.assetsource.data = this.getModels(row)
            }
        }
        else this.assetsource.data = []
    }

    getModels(row) {
        return this.linqset.organisation.models.filter(m => !m.deletedDate).reduce((rows, m) => {
            const modelInsights = m.insights?.find(mi => mi.projectKey === (this.projectKey||CURRENT_STATE_PROJECT_KEY))
            const iaInsights = modelInsights?.informationAssets?.find(ia => ia.groupKey === row._key)
            if (iaInsights) rows.push({name: m.name, modelKey:m._key, ...iaInsights, cost:iaInsights.allocatedCostsByType.total})
            return rows
        }, [])
    }
}
